import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  BookOpen, FileText, Calendar, Bell, TrendingUp,
  CreditCard, CheckCircle, Clock as ClockIcon,
  ChevronRight, Loader2, Camera, Save, Mail, Phone, Shield, MessageSquare,
  IdCard, MapPin, Headphones
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import OphthalmoOnboarding from '@/components/OphthalmoOnboarding';
import InternOnboarding from '@/components/InternOnboarding';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [editForm, setEditForm] = useState({});
  const fileRef = useRef(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const u = JSON.parse(storedUser);
      setUser(u);
      setEditForm({ first_name: u.first_name || '', last_name: u.last_name || '', phone: u.phone || '', city: u.city || '', bio: u.bio || '' });
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/my-orders`)
      .then(r => setOrders(r.data.orders || []))
      .catch(() => {});
  }, []);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/upload/avatar`, fd, { headers: { Authorization: `Bearer ${token}` } });
      const updatedUser = { ...user, profile_image: res.data.url };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      toast.success('Photo de profil mise à jour');
    } catch { toast.error("Erreur lors de l'upload"); }
    setUploadingPhoto(false);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API}/users/${user.id}`, editForm, { headers: { Authorization: `Bearer ${token}` } });
      const updatedUser = { ...user, ...res.data };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setEditMode(false);
      toast.success('Profil mis à jour');
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    setSaving(false);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return "Bon après-midi";
    return 'Bonsoir';
  };

  const getRoleName = (role) => ({
    super_admin: 'Super Administrateur',
    regional: 'Administrateur Régional',
    ophthalmologist: 'Ophtalmologiste',
    intern: 'Interne',
    tresorier: 'Trésorier',
    auteur: 'Auteur',
  }[role] || role);

  const isIntern = user?.role === 'intern';

  const quickActions = [
    { icon: IdCard, label: 'Mes Fiches', path: '/mes-fiches', color: 'from-cyan-500 to-teal-600', desc: 'Gérer mes fiches annuaire' },
    { icon: BookOpen, label: 'Formations', path: '/nos-formations', color: 'from-violet-500 to-purple-600', desc: "Accéder aux cours" },
    { icon: MessageSquare, label: 'Forum', path: '/forum', color: 'from-emerald-500 to-green-600', desc: 'Discussions entre membres' },
    { icon: Calendar, label: "Événements", path: '/events', color: 'from-orange-500 to-amber-600', desc: 'Voir les prochains events' },
    { icon: CreditCard, label: 'Adhésions', path: '/mes-adhesions', color: 'from-blue-500 to-indigo-600', desc: 'Mes reçus et cotisations', hideForIntern: true },
    { icon: FileText, label: 'Publications', path: '/publications', color: 'from-rose-500 to-pink-600', desc: 'Articles scientifiques' },
    { icon: Headphones, label: 'Support', path: '/mon-support', color: 'from-slate-600 to-gray-800', desc: 'Aide et assistance' },
  ].filter(a => !(isIntern && a.hideForIntern));

  const completedOrders = orders.filter(o => o.status === 'completed' && o.amount > 0);

  const profileImageUrl = user?.profile_image
    ? (user.profile_image.startsWith('http') ? user.profile_image : `${window.location.origin}/api${user.profile_image}`)
    : null;

  return (
    <div className="min-h-full bg-[#f8fafb]" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="member-dashboard">
      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* PROFILE CARD */}
        <div className="relative overflow-hidden rounded-2xl mb-8 bg-gradient-to-r from-[#1e3a5f] to-[#35AFE4] shadow-xl" data-testid="profile-section">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute w-[400px] h-[400px] rounded-full bg-white/[0.03]" style={{ right: '-120px', top: '-120px' }} />
            <div className="absolute w-[300px] h-[300px] rounded-full bg-white/[0.03]" style={{ left: '-80px', bottom: '-100px' }} />
          </div>
          <div className="relative z-10 p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <div className="relative group">
                <div className="w-20 h-20 rounded-full overflow-hidden ring-4 ring-white/20 shadow-xl bg-white/10 flex items-center justify-center">
                  {profileImageUrl ? (
                    <img src={profileImageUrl} alt={user?.first_name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white text-2xl font-bold" style={{ fontFamily: "'Barlow', sans-serif" }}>
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </span>
                  )}
                </div>
                <button onClick={() => fileRef.current?.click()}
                  className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center text-[#35AFE4] hover:bg-gray-50 transition-colors border border-gray-100"
                  disabled={uploadingPhoto} data-testid="upload-photo-btn">
                  {uploadingPhoto ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
              </div>

              <div className="flex-1">
                <h1 className="text-xl sm:text-2xl font-bold text-white mb-0.5" style={{ fontFamily: "'Barlow', sans-serif" }}>
                  {getGreeting()}, {user?.first_name} !
                </h1>
                <p className="text-sm text-white/70 font-medium">{getRoleName(user?.role)}</p>
                <div className="flex items-center gap-4 mt-2 text-[11px] text-white/50">
                  {user?.email && <span className="flex items-center gap-1"><Mail size={10} /> {user.email}</span>}
                  {user?.phone && <span className="flex items-center gap-1"><Phone size={10} /> {user.phone}</span>}
                  {user?.subscription_status && (
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${user.subscription_status === 'active' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
                      <Shield size={9} /> {user.subscription_status === 'active' ? "Adhésion active" : "Adhésion inactive"}
                    </span>
                  )}
                </div>
              </div>

              <button onClick={() => setEditMode(!editMode)}
                className="px-4 py-2 rounded-xl text-xs font-bold transition-all bg-white/10 hover:bg-white/20 text-white border border-white/10"
                data-testid="edit-profile-btn">
                {editMode ? 'Annuler' : 'Modifier mon profil'}
              </button>
            </div>

            {editMode && (
              <div className="mt-6 p-5 bg-white/[0.08] rounded-xl border border-white/10 backdrop-blur-sm" data-testid="edit-profile-form">
                <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-white/[0.06] border border-white/[0.08] text-[11px] text-white/70 leading-snug" data-testid="profile-fiche-hint">
                  <Shield size={12} className="text-white/60 flex-shrink-0 mt-0.5" />
                  <p>
                    Ces champs concernent vos <strong>coordonnées personnelles</strong>.
                    Pour modifier les coordonnées de votre <strong>cabinet</strong> (adresse, téléphone consultation),
                    utilisez le menu <em>Mes fiches annuaire</em>.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: 'first_name', label: 'Prénom' },
                    { key: 'last_name', label: 'Nom' },
                    { key: 'phone', label: 'Téléphone' },
                    { key: 'city', label: 'Ville' },
                  ].map(field => (
                    <div key={field.key}>
                      <label className="text-[10px] font-bold text-white/50 uppercase tracking-wider mb-1 block">{field.label}</label>
                      <input value={editForm[field.key] || ''} onChange={e => setEditForm(p => ({ ...p, [field.key]: e.target.value }))}
                        className="w-full px-4 py-2.5 bg-white/10 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 outline-none focus:ring-2 focus:ring-white/20"
                        data-testid={`edit-${field.key}`} />
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex justify-end">
                  <button onClick={handleSaveProfile} disabled={saving}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#1e3a5f] text-xs font-bold rounded-xl hover:bg-gray-100 transition-all disabled:opacity-50 shadow-sm"
                    data-testid="save-profile-btn">
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                    Enregistrer
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* OPHTHALMOLOGIST ONBOARDING WIDGET */}
        {user?.role === 'ophthalmologist' && (
          <OphthalmoOnboarding user={user} />
        )}

        {/* INTERN ONBOARDING WIDGET */}
        {user?.role === 'intern' && (
          <InternOnboarding user={user} />
        )}

        {/* INTERN STUDENT CARD CARD */}
        {isIntern && <InternStudentCardCard user={user} onUpdate={(u) => { setUser(u); localStorage.setItem('user', JSON.stringify(u)); }} />}

        {/* QUICK ACTIONS */}
        <div className="mb-8">
          <h2 className="text-base font-bold text-[#1e3a5f] mb-4" style={{ fontFamily: "'Barlow', sans-serif" }}>Accès rapides</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {quickActions.map((action, i) => (
              <button key={i} onClick={() => navigate(action.path)}
                className="group relative overflow-hidden rounded-xl p-4 bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 text-left"
                data-testid={`quick-action-${action.label.toLowerCase()}`}>
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${action.color} flex items-center justify-center mb-3 shadow-md`}>
                  <action.icon className="text-white" size={20} />
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-0.5">{action.label}</h3>
                <p className="text-[11px] text-gray-500">{action.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* BOTTOM GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-xl p-5 bg-white border border-gray-100 shadow-sm">
            <h2 className="text-sm font-bold text-[#1e3a5f] mb-4 flex items-center gap-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
              <Bell size={16} className="text-[#35AFE4]" /> Activité récente
            </h2>
            <div className="space-y-2">
              {completedOrders.length > 0 ? completedOrders.slice(0, 3).map((o, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                    <CheckCircle className="text-white" size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{o.product || "Adhésion"}</p>
                    <p className="text-[11px] text-gray-500">{o.order_date ? new Date(o.order_date).toLocaleDateString('fr-FR') : ''} — {o.amount?.toFixed(2)} €</p>
                  </div>
                </div>
              )) : (
                <p className="text-xs text-gray-400 py-4 text-center">Aucune activité récente</p>
              )}
            </div>
          </div>

          <div className="rounded-xl p-5 bg-white border border-gray-100 shadow-sm">
            <h2 className="text-sm font-bold text-[#1e3a5f] mb-4 flex items-center gap-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
              <TrendingUp size={16} className="text-emerald-600" /> Ressources
            </h2>
            <div className="space-y-2">
              {[
                { icon: BookOpen, label: 'Formations', desc: 'Cours, replays et ressources', path: '/nos-formations', color: 'from-violet-500 to-purple-600' },
                { icon: FileText, label: 'Publications', desc: 'Articles scientifiques', path: '/publications', color: 'from-orange-500 to-red-500' },
                { icon: MapPin, label: 'Annuaire', desc: 'Praticiens et spécialistes', path: '/annuaire', color: 'from-cyan-500 to-teal-600' },
              ].map((r, i) => (
                <button key={i} onClick={() => navigate(r.path)} className="w-full flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100 hover:border-[#35AFE4]/30 transition-colors text-left group" data-testid={`go-${r.label.toLowerCase()}`}>
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${r.color} flex items-center justify-center shadow-sm`}>
                    <r.icon className="text-white" size={14} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-gray-800">{r.label}</p>
                    <p className="text-[11px] text-gray-500">{r.desc}</p>
                  </div>
                  <ChevronRight size={14} className="text-gray-400 group-hover:text-[#35AFE4] transition-colors" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;

// ===================================================================
// INTERN STUDENT CARD CARD
// Shows current student card status + allows re-upload for annual renewal.
// ===================================================================
const InternStudentCardCard = ({ user, onUpdate }) => {
  const [card, setCard] = useState({
    student_card_url: user?.student_card_url || '',
    student_card_expiry: user?.student_card_expiry || '',
    student_card_status: user?.student_card_status || 'pending',
    student_year: user?.student_year || '',
  });
  const [showRenew, setShowRenew] = useState(false);
  const [file, setFile] = useState(null);
  const [newExpiry, setNewExpiry] = useState('');
  const [newYear, setNewYear] = useState(card.student_year);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/intern/student-card`)
      .then(r => setCard(c => ({ ...c, ...r.data })))
      .catch(() => {});
  }, []);

  const statusConfig = {
    approved: { label: 'Validée', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle },
    pending:  { label: 'En attente de validation', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', icon: ClockIcon },
    rejected: { label: 'Refusée — à refaire', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', icon: Shield },
    expired:  { label: 'Expirée — à renouveler', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', icon: Shield },
  };
  const expiryDate = card.student_card_expiry ? new Date(card.student_card_expiry) : null;
  const now = new Date();
  const expiresSoon = expiryDate && (expiryDate.getTime() - now.getTime() < 60 * 24 * 3600 * 1000) && expiryDate > now;
  const status = expiryDate && expiryDate < now ? 'expired' : (card.student_card_status || 'pending');
  const cfg = statusConfig[status] || statusConfig.pending;
  const Icon = cfg.icon;

  const handleRenew = async () => {
    if (!file) { toast.error('Veuillez sélectionner un fichier'); return; }
    if (!newExpiry) { toast.error('Date d\'expiration requise'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('expiry_date', newExpiry);
      const up = await axios.post(`${API}/upload/student-card`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await axios.post(`${API}/intern/renew-student-card`, {
        student_card_url: up.data.url,
        student_card_expiry: newExpiry,
        student_year: newYear || card.student_year,
      });
      const refreshed = await axios.get(`${API}/intern/student-card`);
      setCard(c => ({ ...c, ...refreshed.data }));
      onUpdate?.({ ...user, ...refreshed.data });
      toast.success('Carte étudiante soumise. En attente de validation par l\'administration.');
      setShowRenew(false);
      setFile(null);
      setNewExpiry('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setBusy(false);
  };

  return (
    <div className={`rounded-xl border ${cfg.border} ${cfg.bg} p-5 mb-6`} data-testid="intern-student-card-block">
      <div className="flex items-start gap-4">
        <div className={`w-11 h-11 rounded-xl bg-white flex items-center justify-center ${cfg.color} flex-shrink-0`}>
          <IdCard size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-base font-bold text-gray-900">Ma carte étudiante</h3>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${cfg.color} bg-white border ${cfg.border}`}>
              <Icon size={11} /> {cfg.label}
            </span>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mt-3 text-xs">
            <div>
              <p className="text-gray-500 mb-0.5">Année de formation</p>
              <p className="font-semibold text-gray-800">{card.student_year || '—'}</p>
            </div>
            <div>
              <p className="text-gray-500 mb-0.5">Valide jusqu'au</p>
              <p className={`font-semibold ${expiresSoon ? 'text-amber-700' : 'text-gray-800'}`}>
                {expiryDate ? expiryDate.toLocaleDateString('fr-FR') : '—'}
              </p>
            </div>
            <div>
              <p className="text-gray-500 mb-0.5">Carte</p>
              {card.student_card_url ? (
                <a href={card.student_card_url} target="_blank" rel="noopener noreferrer"
                  className="font-semibold text-[#35AFE4] hover:underline" data-testid="link-current-card">
                  Voir la carte
                </a>
              ) : <span className="font-semibold text-gray-400">Non transmise</span>}
            </div>
          </div>
          {!showRenew && (
            <button onClick={() => setShowRenew(true)}
              className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition-colors ${
                (status === 'expired' || status === 'rejected' || expiresSoon)
                  ? 'bg-[#35AFE4] text-white hover:bg-[#1e9bd4]'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="btn-renew-card">
              <ClockIcon size={13} />
              {(status === 'expired' || status === 'rejected') ? 'Renouveler ma carte'
                : expiresSoon ? 'Renouveler ma carte (expire bientôt)'
                : 'Mettre à jour'}
            </button>
          )}

          {showRenew && (
            <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="renew-card-form">
              <div>
                <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Nouveau fichier (JPG / PNG / PDF)</label>
                <input type="file" accept="image/*,.pdf" onChange={e => setFile(e.target.files?.[0])}
                  className="mt-1 block w-full text-xs" data-testid="renew-file-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Date d'expiration</label>
                  <input type="date" value={newExpiry} onChange={e => setNewExpiry(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="renew-expiry-input" />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Année de formation</label>
                  <select value={newYear} onChange={e => setNewYear(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="renew-year-input">
                    <option value="">—</option>
                    <option value="DES1">DES 1</option>
                    <option value="DES2">DES 2</option>
                    <option value="DES3">DES 3</option>
                    <option value="DES4">DES 4</option>
                    <option value="DES5">DES 5</option>
                    <option value="DFASM2">DFASM 2</option>
                    <option value="DFASM3">DFASM 3</option>
                    <option value="CCA">CCA / Assistant</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <button onClick={handleRenew} disabled={busy}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1e3a5f] text-white text-xs font-bold hover:bg-[#162d4a] disabled:opacity-50"
                  data-testid="renew-submit-btn">
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}
                  Soumettre
                </button>
                <button onClick={() => { setShowRenew(false); setFile(null); }}
                  className="text-xs text-gray-400 hover:text-gray-600 px-2" data-testid="renew-cancel-btn">
                  Annuler
                </button>
              </div>
              <p className="text-[11px] text-amber-700 bg-amber-50 rounded p-2">
                Votre compte passera en statut "en attente" jusqu'à validation par un administrateur.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
